import { Suspense, type ReactNode } from 'react'
import { Navigate, useRouteError } from 'react-router-dom'
import { useAuth } from '@/shared/auth/AuthContext'
import type { ModuleKey } from '@/shared/api/types'
import { Button, Card, EmptyState, Skeleton } from '@/shared/ui/primitives'
import { homeFor } from './navigation'

export function Screen({ children, module }: { children: ReactNode; module?: ModuleKey }) {
  const { hasModule } = useAuth()
  if (module && !hasModule(module)) {
    return (
      <Card>
        <EmptyState
          icon="lock"
          title="Module non souscrit"
          message={`Le module « ${module} » n'est pas activé sur cette installation. Le superadmin peut l'ouvrir depuis Administration › Modules.`}
        />
      </Card>
    )
  }
  return (
    <Suspense
      fallback={
        <Card>
          <Skeleton lines={6} />
        </Card>
      }
    >
      {children}
    </Suspense>
  )
}

export function Protected({ children }: { children: ReactNode }) {
  const { user, booting } = useAuth()
  if (booting)
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Skeleton lines={5} />
        </Card>
      </div>
    )
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

export function Home() {
  const { user } = useAuth()
  return <Navigate to={user ? homeFor(user.role) : '/login'} replace />
}

export function RouteError() {
  const err = useRouteError()
  const message = err instanceof Error ? err.message : 'Une erreur inattendue est survenue.'
  return (
    <div style={{ padding: 24 }}>
      <Card>
        <EmptyState
          icon="warn"
          title="Cette page n'a pas pu s'afficher"
          message={message}
          action={
            <Button variant="primary" onClick={() => window.location.assign('/')}>
              Revenir à l'accueil
            </Button>
          }
        />
      </Card>
    </div>
  )
}

export function NotFound() {
  return (
    <Card>
      <EmptyState
        icon="search"
        title="Page introuvable"
        message="Le lien que vous avez suivi ne correspond à aucun écran."
        action={
          <Button variant="primary" onClick={() => window.history.back()}>
            Revenir en arrière
          </Button>
        }
      />
    </Card>
  )
}
